import { useEffect, useMemo, useState } from 'react';
import {
  Input,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Switch,
} from '@vnetwork/ui/primitives';
import { Field } from '@vnetwork/ui/forms';
import { FormSectionCard } from '../../event-jobs/event-job-create/FormSectionCard';
import type { LogoConfig } from '@/api/media/live-linear-jobs-types';
import styles from './LinearJobCreate.module.css';

const UNIT_OPTIONS = ['%', 'px'];
const HORIZONTAL_ALIGN_OPTIONS = ['left', 'right'];
const VERTICAL_ALIGN_OPTIONS = ['top', 'bottom'];
const SIZING_POLICY_OPTIONS = [{ value: '0', label: 'Shrink to Fit' }];

function isPreviewUrl(value?: string) {
  if (!value?.trim()) return false;
  try {
    const p = new URL(value.trim());
    return ['http:', 'https:'].includes(p.protocol);
  } catch {
    return false;
  }
}

function PreviewPanel({ logo }: { logo: LogoConfig }) {
  const [imgError, setImgError] = useState(false);
  const hasPreview = isPreviewUrl(logo.file_path);

  useEffect(() => { setImgError(false); }, [logo.file_path]);

  const width = `${logo.width || 15}${logo.width_unit || '%'}`;
  const height = `${logo.height || 15}${logo.height_unit || '%'}`;
  const dx = `${logo.dx || 0}${logo.dx_unit || 'px'}`;
  const dy = `${logo.dy || 0}${logo.dy_unit || 'px'}`;
  const horiz = logo.horizontal_align || 'left';
  const vert = logo.vertical_align || 'top';

  const imagePos = useMemo(() => {
    const sx = horiz === 'right' ? { right: dx } : { left: dx };
    const sy = vert === 'bottom' ? { bottom: dy } : { top: dy };
    return { ...sx, ...sy } as React.CSSProperties;
  }, [dx, dy, horiz, vert]);

  return (
    <div className={styles.previewPanel}>
      <p className={styles.previewLabel}>Preview</p>
      <div className={styles.previewCanvas}>
        <div className={styles.previewGrid} />
        {!hasPreview && (
          <div className={styles.previewPlaceholder}>
            <span>Enter a logo URL to preview the watermark</span>
          </div>
        )}
        {hasPreview && imgError && (
          <div className={styles.previewError}>
            <span>Unable to load logo preview</span>
          </div>
        )}
        {hasPreview && !imgError && (
          <img
            src={logo.file_path}
            alt="Logo preview"
            className={styles.previewImg}
            style={{ width, height, ...imagePos }}
            onError={() => setImgError(true)}
            onLoad={() => setImgError(false)}
          />
        )}
      </div>
      <div className={styles.previewMeta}>
        <span>Size: {width} × {height}</span>
        <span>Position: {horiz} / {vert}</span>
        <span>Offset: {dx} / {dy}</span>
      </div>
    </div>
  );
}

interface ValueUnitFieldProps {
  valueLabel: string;
  unitLabel: string;
  value?: string;
  unit?: string;
  placeholder: string;
  onValueChange: (v: string) => void;
  onUnitChange: (v: string) => void;
}

function ValueUnitField({ valueLabel, unitLabel, value, unit, placeholder, onValueChange, onUnitChange }: ValueUnitFieldProps) {
  return (
    <div className={styles.valueUnitGroup}>
      <Field label={valueLabel}>
        {(fp) => (
          <Input
            {...fp}
            value={value || ''}
            placeholder={placeholder}
            onChange={(e) => onValueChange(e.target.value)}
          />
        )}
      </Field>
      <Field label={unitLabel}>
        {(fp) => (
          <Select value={unit || 'px'} onValueChange={onUnitChange}>
            <SelectTrigger {...fp}><SelectValue /></SelectTrigger>
            <SelectContent>
              {UNIT_OPTIONS.map((o) => <SelectItem key={o} value={o}>{o}</SelectItem>)}
            </SelectContent>
          </Select>
        )}
      </Field>
    </div>
  );
}

interface LogoWatermarkSectionProps {
  logo: LogoConfig;
  errors: Record<string, string>;
  onChange: (next: LogoConfig) => void;
  clearError: (field: string) => void;
}

/** Optional logo/watermark overlay — file path, size, position, offset, live preview. */
export function LogoWatermarkSection({ logo, errors, onChange, clearError }: LogoWatermarkSectionProps) {
  const update = (patch: Partial<LogoConfig>) => {
    onChange({ ...logo, ...patch });
    Object.keys(patch).forEach((k) => clearError(`logo.${k}`));
  };

  return (
    <FormSectionCard
      title="Logo / Watermark"
      description="Optionally overlay a logo or watermark on the linear output."
      actions={
        <label className={styles.switchItem} htmlFor="logo-toggle">
          <span>Enable watermark overlay</span>
          <Switch checked={logo.enabled} onCheckedChange={(v) => update({ enabled: v })} id="logo-toggle" />
        </label>
      }
    >
      {logo.enabled && (
        <div className={styles.logoBody}>
          <div className={styles.logoFields}>
            {/* Source */}
            <Field label="Logo File Path" required error={errors['logo.file_path']}>
              {(fp) => (
                <Input
                  {...fp}
                  value={logo.file_path || ''}
                  placeholder="https://cdn.example.com/logo.png"
                  onChange={(e) => update({ file_path: e.target.value })}
                />
              )}
            </Field>

            {/* Size */}
            <p className={styles.subLabel}>Size</p>
            <div className={styles.grid2}>
              <ValueUnitField
                valueLabel="Width" unitLabel="Unit" value={logo.width} unit={logo.width_unit || '%'}
                placeholder="15" onValueChange={(v) => update({ width: v })} onUnitChange={(v) => update({ width_unit: v })}
              />
              <ValueUnitField
                valueLabel="Height" unitLabel="Unit" value={logo.height} unit={logo.height_unit || '%'}
                placeholder="15" onValueChange={(v) => update({ height: v })} onUnitChange={(v) => update({ height_unit: v })}
              />
            </div>

            <Field label="Sizing Policy">
              {(fp) => (
                <Select value={logo.sizing_policy || '0'} onValueChange={(v) => update({ sizing_policy: v })}>
                  <SelectTrigger {...fp}><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {SIZING_POLICY_OPTIONS.map((o) => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              )}
            </Field>

            {/* Position */}
            <p className={styles.subLabel}>Position</p>
            <div className={styles.grid2}>
              <Field label="Horizontal Align">
                {(fp) => (
                  <Select value={logo.horizontal_align || 'left'} onValueChange={(v) => update({ horizontal_align: v })}>
                    <SelectTrigger {...fp}><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {HORIZONTAL_ALIGN_OPTIONS.map((o) => <SelectItem key={o} value={o}>{o}</SelectItem>)}
                    </SelectContent>
                  </Select>
                )}
              </Field>
              <Field label="Vertical Align">
                {(fp) => (
                  <Select value={logo.vertical_align || 'top'} onValueChange={(v) => update({ vertical_align: v })}>
                    <SelectTrigger {...fp}><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {VERTICAL_ALIGN_OPTIONS.map((o) => <SelectItem key={o} value={o}>{o}</SelectItem>)}
                    </SelectContent>
                  </Select>
                )}
              </Field>
            </div>

            {/* Offset */}
            <p className={styles.subLabel}>Offset</p>
            <div className={styles.grid2}>
              <ValueUnitField
                valueLabel="DX" unitLabel="Unit" value={logo.dx} unit={logo.dx_unit || 'px'}
                placeholder="20" onValueChange={(v) => update({ dx: v })} onUnitChange={(v) => update({ dx_unit: v })}
              />
              <ValueUnitField
                valueLabel="DY" unitLabel="Unit" value={logo.dy} unit={logo.dy_unit || 'px'}
                placeholder="20" onValueChange={(v) => update({ dy: v })} onUnitChange={(v) => update({ dy_unit: v })}
              />
            </div>
          </div>

          <PreviewPanel logo={logo} />
        </div>
      )}
    </FormSectionCard>
  );
}
